import React, { useState, forwardRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  Grid,
  CircularProgress,
  Card,
  Tabs,
  Tab,
  Tooltip,
  Typography,
} from '@mui/material';
import Dropzone from 'react-dropzone';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { CONTENT_ALIAS, ACTIONS, ASSET_TYPE } from '../../config/const.config';
import {
  getAssetTypeFromFile,
  validateMultiUploadFiles,
} from '../../utils/file-validations.util';
import {
  downloadVideoAsset,
  toggleStatus,
  uploadAssetFile,
  viewVideoAssetById,
} from '../../services/content.service';
import ListData from '../table-elements/list-data.component';
import {
  LISTING_ACTIONS,
  LISTING_COLUMNS,
} from '../../config/module-configs/asset.config';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const LanguageWiseBulkUploadAssets = ({ title, dataId, onCancel }) => {
  const columns = LISTING_COLUMNS;
  const actions = LISTING_ACTIONS;
  const [options, setOptions] = useState({
    rows: [],
    languages: [],
    reloadCounter: 0,
    error: false,
    loading: true,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [assetType, setAssetType] = useState(ASSET_TYPE.SLIDES.value);
  const [selectedFilesArr, setSelectedFilesArr] = useState([]);
  const [showUploading, setShowUploading] = useState(false);
  const [hasFileTypeError, setHasFileTypeError] = useState(false);
  const [downloadingAssets, setDownloadingAssets] = useState({});

  const filteredRows = useMemo(
    () => options.rows.filter((row) => row.assetType === assetType),
    [options.rows, assetType]
  );

  // eslint-disable-next-line no-promise-executor-return
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleSelectFiles = async (files) => {
    setHasFileTypeError(false);
    setSelectedFilesArr(files);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    await sleep(100);

    const validationError = validateMultiUploadFiles(files, options.languages);
    if (validationError) {
      setHasFileTypeError(true);
      setSnackbarInfo({
        show: true,
        type: 'error',
        message: validationError,
      });
    }
  };

  const handleUploadFiles = async (videoId) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    await sleep(100);

    const validationError = validateMultiUploadFiles(
      selectedFilesArr,
      options.languages
    );
    if (validationError) {
      setSnackbarInfo({
        show: true,
        type: 'error',
        message: validationError,
      });
    } else {
      setShowUploading(true);

      try {
        const audioPdfFormData = new FormData();
        const assetTypes = [];
        for (let loop = 0; loop < selectedFilesArr.length; loop += 1) {
          audioPdfFormData.append('files[]', selectedFilesArr[loop]);
          audioPdfFormData.append(
            'filesArr[]',
            JSON.stringify({
              fileName: selectedFilesArr[loop].name,
              language: selectedFilesArr[loop].name.split('-')[0].toUpperCase(),
            })
          );

          const fileAssetType = getAssetTypeFromFile(
            selectedFilesArr[loop].name
          );
          assetTypes.push(fileAssetType);
        }

        assetTypes.forEach((type) => {
          audioPdfFormData.append('assetType[]', type);
        });
        audioPdfFormData.append('videoId', Number(videoId));

        const fileUploadHeader = await uploadAssetFile(audioPdfFormData, true);

        if (Number(fileUploadHeader.data.filesUploaded) > 0) {
          setSelectedFilesArr([]);
          setOptions({
            ...options,
            reloadCounter: options.reloadCounter + 1,
          });
          setSnackbarInfo({
            show: true,
            type: 'success',
            message: `${fileUploadHeader.data.filesUploaded} file${
              fileUploadHeader.data.filesUploaded > 1 ? 's' : ''
            } uploaded sucessfully`,
          });
        } else {
          setSnackbarInfo({
            show: true,
            type: 'error',
            message: 'Something went wrong! Please try again.',
          });
        }
      } catch {
        setSnackbarInfo({
          show: true,
          type: 'error',
          message: 'Something went wrong! Please try again.',
        });
      }
      setShowUploading(false);
    }
  };

  const handleToggleStatus = (r, value) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      id: r.id,
      status: value ? 0 : 1,
      type: 'video-asset',
    };

    toggleStatus(payload)
      .then(() => {
        setOptions((prev) => ({
          ...prev,
          rows: prev.rows.map((row) =>
            row.id === r.id &&
            row.assetType === r.assetType &&
            row.languageId === r.languageId
              ? {
                  ...row,
                  status: value ? 1 : 0,
                }
              : row
          ),
        }));
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Status updated.',
        });
      })
      .catch((e) => {
        setOptions((prev) => ({
          ...prev,
          rows: prev.rows.map((row) =>
            row.id === r.id &&
            row.assetType === r.assetType &&
            row.languageId === r.languageId
              ? {
                  ...row,
                  status: r.status,
                }
              : row
          ),
        }));
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: e.response?.data?.message,
        });
      });
  };

  const handleDownloadFile = async (rowId, languageId) => {
    setDownloadingAssets((prev) => ({
      ...prev,
      [rowId]: true,
    }));

    const params = [];
    params.push(`type=${assetType}`);
    params.push(`langId=${languageId}`);

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    downloadVideoAsset(dataId, paramsQuery)
      .then((res) => {
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);

        let fileName = '';
        const disposition = res.headers['content-disposition'];
        if (disposition) {
          const match = disposition.match(/filename="?(.+?)"?$/);
          if (match) {
            [, fileName] = match;
          }
        }

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      })
      .catch((e) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: e.response?.data?.message || 'Download failed',
        });
      })
      .finally(() => {
        setDownloadingAssets((prev) => ({
          ...prev,
          [rowId]: false,
        }));
      });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    if (selectedAction === ACTIONS.VIEW.value) {
      window.open(`${selectedData.fileUrl}?view=1`, '_blank');
      return;
    }
    if (selectedAction === ACTIONS.DOWNLOAD.value) {
      handleDownloadFile(selectedData.id, selectedData.languageId);
      return;
    }
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };

  const getActionLoadingState = (action, row) =>
    action === ACTIONS.DOWNLOAD.value && Boolean(downloadingAssets[row.id]);

  useEffect(() => {
    viewVideoAssetById(dataId)
      .then((res) => {
        setOptions({
          ...options,
          loading: false,
          rows: res?.data?.rows || [],
          languages: res?.data?.languages || [],
          error: false,
        });
      })
      .catch((err) => {
        setOptions({
          ...options,
          loading: false,
          rows: [],
          languages: [],
          error: true,
        });

        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          show: true,
          type: 'error',
          message,
        });
      });
  }, [dataId, options.reloadCounter]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="view-dialog-title">{`${CONTENT_ALIAS.VIDEO_PLURAL} - ${title}`}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        <form id="upload-assets-form">
          <Box
            component="section"
            sx={{
              p: 3,
              border: '2px solid lightgrey',
              borderRadius: '5px',
              width: '100%',
            }}
          >
            <Grid container spacing={2}>
              <Grid
                item
                display="flex"
                xs={12}
                sm={9}
                md={9}
                direction="column"
                margin="auto"
              >
                <Grid item xs={12} sm={8} md={8}>
                  <Grid item xs={12} sm={12} md={12}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography variant="subtitle2">
                        Add / Replace Files
                      </Typography>
                      <Tooltip
                        title={
                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              gutterBottom
                            >
                              Upload format
                            </Typography>

                            <Typography
                              variant="caption"
                              display="block"
                              gutterBottom
                            >
                              {'<ISO639>-<media>-<book>-<lesson>'}
                            </Typography>

                            <Typography variant="caption" display="block">
                              Examples:
                            </Typography>

                            <Typography variant="caption" display="block">
                              en-slide-genesis-l01.pdf
                            </Typography>
                            <Typography variant="caption" display="block">
                              en-audio-genesis-l01.mp3
                            </Typography>
                            <Typography variant="caption" display="block">
                              en-text-genesis-l01.pdf
                            </Typography>
                            <Typography variant="caption" display="block">
                              en-text-genesis-l01.docx
                            </Typography>
                          </Box>
                        }
                        arrow
                      >
                        <IconButton size="small">
                          <Iconify icon="ep:info-filled" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Dropzone
                      onDrop={(files) => handleSelectFiles(files)}
                      disabled={showUploading}
                    >
                      {({ getRootProps, getInputProps }) => (
                        <section>
                          <div {...getRootProps()}>
                            <input {...getInputProps()} />

                            <Box
                              component="section"
                              sx={{
                                p: 1,
                                border: '1px dashed grey',
                                borderRadius: '20px',
                                width: '100%',
                                marginTop: 1,
                              }}
                            >
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={12}>
                                  <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      textAlign: 'center',
                                    }}
                                  >
                                    <Iconify
                                      icon="mdi:cloud-upload-outline"
                                      width={42}
                                      sx={{ color: 'text.secondary' }}
                                    />
                                    Drag and drop files here, or click the
                                    button below
                                  </Grid>

                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    color="primary.main"
                                    mt={1}
                                  >
                                    <Typography variant="body2" display="block">
                                      {selectedFilesArr.map((key) => (
                                        <Typography
                                          variant="caption"
                                          display="block"
                                        >
                                          {key.name}
                                        </Typography>
                                      ))}
                                    </Typography>
                                  </Box>

                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    p={1}
                                  >
                                    <Button
                                      component="label"
                                      variant="contained"
                                      sx={{
                                        backgroundColor: '#808080',
                                      }}
                                      size="small"
                                      disabled={showUploading}
                                    >
                                      Browse Files
                                    </Button>
                                  </Box>

                                  <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      textAlign: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      display="block"
                                    >
                                      Supported formats: PDF, MP3, DOCX
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Box>
                          </div>
                        </section>
                      )}
                    </Dropzone>
                  </Grid>

                  <Box display="flex" justifyContent="center" p={1}>
                    {!showUploading ? (
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={hasFileTypeError}
                        onClick={() => handleUploadFiles(dataId)}
                        startIcon={<Iconify icon="mdi:cloud-upload" />}
                        size="small"
                      >
                        Upload Files
                      </Button>
                    ) : (
                      <Box
                        display="flex"
                        alignItems="center"
                        padding="4px 10px"
                        borderRadius="6px"
                        sx={{
                          backgroundColor: '#919eab3d',
                          color: '#919eabcc',
                        }}
                        fontSize={14}
                      >
                        <CircularProgress
                          disableShrink
                          size="14px"
                          sx={{ marginRight: 1, color: '#919eabcc' }}
                        />
                        <span>Uploading...</span>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Tabs
                value={assetType}
                onChange={(_, val) => setAssetType(val)}
                aria-label="asset-type-tabs"
              >
                {Object.keys(ASSET_TYPE).map((asset, idx) => (
                  <Tab
                    key={idx + 1}
                    value={ASSET_TYPE[asset].value}
                    label={ASSET_TYPE[asset].label}
                    icon={
                      <IconButton disableRipple>
                        <Iconify icon={ASSET_TYPE[asset].icon} />
                      </IconButton>
                    }
                  />
                ))}
              </Tabs>

              <Grid item xs={12} sm={12} md={12}>
                <Card>
                  <ListData
                    columns={columns}
                    rows={filteredRows}
                    page={null}
                    rowsPerPage={null}
                    totalRows={
                      !options.rows.filter((row) => row.assetType === assetType)
                        .length
                        ? 0
                        : null
                    }
                    loading={options.loading}
                    actions={actions}
                    error={options.error}
                    sortBy={null}
                    sortOrder={null}
                    onPageChange={() => {}}
                    onRowsPerPageChange={() => {}}
                    onSortChange={() => {}}
                    onAction={handleActionSelect}
                    handleToggleStatus={handleToggleStatus}
                    getActionLoadingState={getActionLoadingState}
                  />
                </Card>
              </Grid>
            </Grid>
          </Box>
        </form>
      </DialogContent>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

LanguageWiseBulkUploadAssets.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default LanguageWiseBulkUploadAssets;
